import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  getAlarmsNotifications,
  deleteAlarmNotification
} from "../../redux/services/alarmnotification.service.js";

const AlarmNotification = () => {
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const alarmNotiTriggerKey = useSelector(
    (state) => state.alarmNotificationActions.alarmNotiTriggerKey
  );

  const [alarms, setAlarms] = useState({ upcoming: [], passed: [] });
  const [pageSize] = useState(5);
  const [counts, setCounts] = useState({ upcoming: 5, passed: 5 });
  const [totals, setTotals] = useState({ upcoming: 0, passed: 0 });
  const [loading, setLoading] = useState({ upcoming: false, passed: false });
  const [hiddenRows, setHiddenRows] = useState([]);

  // Format Date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(
      2,
      "0"
    )}/${String(date.getFullYear()).slice(-2)}`;
  };

  const handleHideNotification = async (actionId) => {
    try {
      const response = await deleteAlarmNotification(actionId);
      if (response.statusCode === 200) {
        toast.success("Action Removed from list Successfully");
        setHiddenRows((prev) => [...prev, actionId]);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Something went wrong!"
      });
    }
  };

  // Fetch Alarms
  useEffect(() => {
    let cancelled = false;
    const fetchAlarms = async () => {
      const officerId = user?.webUserId;
      setLoading({ upcoming: true, passed: true });
      try {
        const response = await getAlarmsNotifications(officerId, counts.passed, counts.upcoming);
        if (cancelled) return;
        if (response.statusCode === 200) {
          const alarmsData = response.data.data || [];
          const passed = alarmsData.filter((a) => a.isDatePassed);
          const upcoming = alarmsData.filter((a) => !a.isDatePassed);
          setAlarms({ upcoming, passed });
          setTotals({
            passed: response.data.datePassedRecord || 0,
            upcoming: response.data.upcomingRecord || 0
          });
        } else {
          Swal.fire({ icon: "error", title: "Oops...", text: "Something went wrong!" });
        }
      } catch (error) {
        if (!cancelled) {
          Swal.fire({
            icon: "error",
            title: "Alarm Notification Not Available",
            text: error?.response?.data?.message || "Internal server error (500)"
          });
        }
      } finally {
        if (!cancelled) setLoading({ upcoming: false, passed: false });
      }
    };

    fetchAlarms();
    return () => {
      cancelled = true;
    };
  }, [counts, hiddenRows, alarmNotiTriggerKey, user?.webUserId]);

  // Load More Handler
  const handleLoadMore = (type) => {
    if (counts[type] < totals[type]) {
      setCounts((prev) => ({ ...prev, [type]: prev[type] + pageSize }));
    }
  };

  const handleNotificationClick = (AlarmNotificationId, caseManagerDetailId, actionDesc) => {
    const cleanDesc = actionDesc.includes(" - ")
      ? actionDesc.split(" - ").slice(1).join(" - ")
      : actionDesc;

    dispatch({
      type: "UPDATE_SELECTED_ALARM_NOTIFICATION",
      payload: { id: AlarmNotificationId, desc: cleanDesc }
    });

    window.open(`/actionDiary/list/${caseManagerDetailId}`, "_blank");
  };

  const renderNotifications = (type, bgClass, title) => {
    const list = alarms[type];
    const isLoading = loading[type];
    const total = totals[type];
    const count = counts[type];

    return (
      <div className="accordion-item">
        <h2 className="accordion-header">
          <button
            className={`accordion-button ${bgClass} shadow-none py-2`}
            type="button"
            data-bs-toggle="collapse"
            data-bs-target={`#collapse-${type}`}
            aria-expanded={type === "upcoming"}
            aria-controls={`collapse-${type}`}>
            <strong>{title}</strong>
          </button>
        </h2>
        <div
          id={`collapse-${type}`}
          className={`accordion-collapse collapse ${type === "upcoming" ? "show" : ""}`}
          data-bs-parent="#accordionExample">
          <div className="accordion-body p-0 py-1">
            {list.length > 0 ? (
              list.map(
                (alarm) =>
                  alarm.actionItemDetailId && (
                    <li
                      key={alarm.actionItemDetailId}
                      className={hiddenRows.includes(alarm.actionItemDetailId) ? "d-none" : ""}>
                      <div className="row px-3">
                        <div className="col-md-12 px-1">
                          <div
                            role="button"
                            className="d-flex justify-content-between align-items-center position-relative"
                            style={{ cursor: "pointer" }}
                            onClick={() =>
                              handleNotificationClick(
                                alarm.actionItemDetailId,
                                alarm.caseManagerDetailId,
                                alarm.actionItemDetail
                              )
                            }>
                            <div className="notification-text">
                              <p className="notification-title m-0 black-white-text">
                                <small>
                                  <strong>
                                    #{alarm.caseManagerDetailId} - {alarm.actionItemDetail}
                                  </strong>
                                </small>
                              </p>
                              <p className="notification-subtitle text-sm m-0 black-white-text">
                                <small>
                                  Schedule Date:{" "}
                                  {alarm.scheduledDate ? formatDate(alarm.scheduledDate) : "N/A"}
                                </small>
                              </p>
                            </div>
                            <span className="badge square-pill bg-light p-1">
                              <i
                                className="bi bi-x"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleHideNotification(alarm.actionItemDetailId);
                                }}></i>
                            </span>
                          </div>
                        </div>
                      </div>
                      <hr className="m-1" />
                    </li>
                  )
              )
            ) : (
              <li>
                <div className="text-center p-3">No Data for {title}</div>
              </li>
            )}
            {count < total && (
              <div className="d-flex justify-content-end">
                <button className="btn btn-primary btn-sm" onClick={() => handleLoadMore(type)}>
                  {isLoading && (
                    <span className="spinner-border spinner-border-sm me-2" role="status" />
                  )}
                  Load More
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <ul className="navbar-nav ms-auto mb-lg-0">
      <li className="nav-item dropdown me-3 alarm-nav-link">
        <Link
          className="nav-link active dropdown-toggle text-primary"
          data-bs-toggle="dropdown"
          aria-expanded="false">
          <i className="bi bi-alarm-fill fs-4"></i>
          <span className="position-absolute translate-right badge rounded-pill bg-info m-0 p-0">
            <div className="btn-group position-absolute translate-middle badge rounded-pill">
              <button type="button" className="btn btn-warning btn-sm py-0 px-1 mt-1">
                <small>
                  {loading.upcoming ? (
                    <span className="spinner-border spinner-border-sm" />
                  ) : (
                    totals.upcoming
                  )}
                </small>
              </button>
              <button type="button" className="btn btn-danger btn-sm py-0 px-1 mt-1">
                <small>
                  {loading.passed ? (
                    <span className="spinner-border spinner-border-sm" />
                  ) : (
                    totals.passed
                  )}
                </small>
              </button>
            </div>
          </span>
        </Link>

        <ul
          className="alarm-noti-dropdown dropdown-menu dropdown-menu-end notification-dropdown border"
          onClick={(e) => e.stopPropagation()}>
          <div className="accordion" id="accordionExample">
            {renderNotifications("upcoming", "bg-warning", "Upcoming Actions")}
            {renderNotifications("passed", "bg-danger", "Past Actions")}
          </div>
        </ul>
      </li>
    </ul>
  );
};

export default AlarmNotification;
